/**
 * `GooglePlacesPicker` — shared city / address autocomplete input.
 *
 * The component name is legacy: the picker now runs on **Nominatim**
 * (OpenStreetMap) rather than Google Places. The exported interface
 * (`GooglePlace`, `parsePlace`, props, imperative handle) is preserved so
 * callers (`Register.tsx`, `EditProfile.tsx`) require zero edits.
 *
 * How it works:
 *  - typing debounces 350 ms then calls `searchNominatim(input)`
 *  - selecting a hit builds a `google.maps.places.PlaceResult`-shaped shim via
 *    `toPlaceResult(hit)` and feeds it into `parsePlace()` — the extractor is
 *    kept byte-for-byte from the previous Google-Places implementation so the
 *    parsed `{ address, city, state, country, zipcode, latitude, longitude,
 *    country_name, location }` payload is identical.
 *
 * Failure modes degrade gracefully: if Nominatim throws or the caller passes
 * `status !== 'ready'`, the input keeps working as a plain text box.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type InputHTMLAttributes,
} from 'react';
import { Loader2, MapPin } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useGoogleMaps } from '@/pages/places/hooks/useGoogleMaps';
import {
  searchNominatim,
  splitDisplayName,
  cleanDisplayName,
  toPlaceResult,
  type NominatimHit,
} from '@/pages/places/lib/nominatim';

/** Parsed result returned to the caller via `onSelect`. */
export interface GooglePlace {
  /** Best-effort street-address portion (without city/state/country). */
  address: string;
  city: string;
  state: string;
  /** ISO short_name, e.g. `'US'`, `'IN'`. */
  country: string;
  /** Full long_name of the country, e.g. `'United States'`. */
  country_name: string;
  zipcode: string;
  latitude: number | null;
  longitude: number | null;
  /** Composed display string (address, city, state, country). */
  location: string;
}

export interface GooglePlacesPickerProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'onSelect' | 'value' | 'defaultValue'
  > {
  /** Controlled text value (e.g. the saved `city` string). */
  value?: string;
  /** Fired on every keystroke (debounced predictions are independent). */
  onChange?: (value: string) => void;
  /** Fired when the user picks a prediction and Places resolves the details. */
  onSelect?: (place: GooglePlace) => void;
  /** Wrapper className (the input itself takes `inputClassName`). */
  className?: string;
  inputClassName?: string;
}

/** Strip leading directional / municipal suffix noise from a city name. */
function stripCityNoise(name: string): string {
  return name
    .replace(/\b(West|East|North|South|Division)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Strip the "Division" suffix from a state name (mirrors legacy behaviour). */
function stripStateNoise(name: string): string {
  return name.replace(/\b(Division)\b/g, '').replace(/\s+/g, ' ').trim();
}

/** Trim the trailing administrative words from a city ("City", "County", …). */
function trimCityWords(name: string): string {
  return name.replace(/\b(City|County|Metro)\b/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Parse a `PlaceResult` into the canonical `GooglePlace` shape. Pulled out of
 * the component so it can be unit-tested without rendering.
 */
export function parsePlace(place: google.maps.places.PlaceResult): GooglePlace {
  const components = place.address_components ?? [];
  const addressParts: string[] = [];
  let city = '';
  let state = '';
  let country = '';
  let countryName = '';
  let zipcode = '';

  for (const comp of components) {
    const types = comp.types ?? [];

    const isAddressPart =
      !comp.long_name.includes('Unnamed Road') &&
      !types.includes('plus_code') &&
      (types.includes('street_number') ||
        types.includes('route') ||
        types.includes('sublocality') ||
        types.includes('sublocality_level_1') ||
        types.includes('sublocality_level_2') ||
        types.includes('sublocality_level_3') ||
        types.includes('premise'));
    if (isAddressPart) addressParts.push(comp.long_name);

    if (types.includes('locality')) {
      city = comp.long_name.trim();
    }
    if (types.includes('administrative_area_level_3')) {
      city = stripCityNoise(comp.long_name);
    }
    if (!city && types.includes('administrative_area_level_2')) {
      city = stripCityNoise(comp.long_name);
    }
    if (types.includes('administrative_area_level_1')) {
      state = stripStateNoise(comp.long_name);
    }
    if (types.includes('country')) {
      country = comp.short_name;
      countryName = comp.long_name;
    }
    if (types.includes('postal_code')) {
      zipcode = comp.long_name;
    }
  }

  // Legacy fallback: if no recognised address parts, use the first component.
  if (
    addressParts.length === 0 &&
    components[0] &&
    !(components[0].types ?? []).includes('plus_code')
  ) {
    addressParts.push(components[0].long_name);
  }

  if (city) city = trimCityWords(city);

  // Don't repeat the city inside the street-address string.
  const dedupedAddressParts = Array.from(
    new Set(
      addressParts.filter(
        (a) => a.toLowerCase() !== (city || '').toLowerCase(),
      ),
    ),
  );

  // Prefer the viewport center over `geometry.location` for region-shaped
  // picks (cities, districts, states). Google's `location` is its
  // "representative point" — for some localities (e.g. "Patiala") it
  // returns a point that's actually inside a neighboring metro
  // (Chandigarh in that case), which propagates into `map_lat`/`map_lng`
  // and breaks distance-based features. The viewport is the place's
  // bounding box; its center is reliably inside the picked entity.
  // For specific addresses (street_number/premise/route) the viewport is
  // already tight around `location`, so the values agree.
  const isRegionPick = (place.types ?? []).some((t) =>
    /^(locality|administrative_area_level_\d|country|postal_code|sublocality|neighborhood)$/.test(t),
  );
  const viewport = place.geometry?.viewport;
  let latitude: number | null = null;
  let longitude: number | null = null;
  if (isRegionPick && viewport) {
    const center = viewport.getCenter();
    latitude = center.lat();
    longitude = center.lng();
  } else {
    const loc = place.geometry?.location;
    latitude = loc?.lat() ?? null;
    longitude = loc?.lng() ?? null;
  }

  // Compose the human-readable label.
  const parts: string[] = [];
  if (dedupedAddressParts.length) parts.push(dedupedAddressParts.join(', '));
  if (city) parts.push(city);
  if (state) parts.push(state);
  if (country) parts.push(country);
  const location = Array.from(new Set(parts)).join(', ');

  return {
    address: dedupedAddressParts.join(', '),
    city,
    state,
    country,
    country_name: countryName,
    zipcode,
    latitude,
    longitude,
    location,
  };
}

/** Imperative handle so callers (e.g. RHF refs) can focus / clear the input. */
export interface GooglePlacesPickerHandle {
  focus: () => void;
  clear: () => void;
}

export const GooglePlacesPicker = forwardRef<
  GooglePlacesPickerHandle,
  GooglePlacesPickerProps
>(function GooglePlacesPicker(
  {
    value,
    onChange,
    onSelect,
    placeholder = 'Start typing a city or address…',
    className,
    inputClassName,
    disabled,
    ...rest
  },
  ref,
) {
  const { status } = useGoogleMaps();
  const ready = status === 'ready';

  const [input, setInput] = useState<string>(value ?? '');
  const [options, setOptions] = useState<NominatimHit[]>([]);
  const [open, setOpen] = useState(false);
  const [fetching, setFetching] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => inputRef.current?.focus(),
      clear: () => {
        setInput('');
        setOptions([]);
        onChange?.('');
      },
    }),
    [onChange],
  );

  // Sync external `value` prop into our controlled input.
  useEffect(() => {
    if (value !== undefined && value !== input) setInput(value);
    // We intentionally only sync when the prop reference changes — local typing
    // updates `input` directly via the change handler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounced prediction fetch.
  const fetchPredictions = useCallback((next: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (!next.trim()) {
        setOptions([]);
        return;
      }
      searchNominatim(next)
        .then((hits) => setOptions(hits))
        .catch(() => {
          // Nominatim is a public shared server; a transient failure just
          // means the user keeps typing free-form text. Nothing to surface.
          setOptions([]);
        });
    }, 350);
  }, []);

  useEffect(() => {
    if (ready) fetchPredictions(input);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [input, ready, fetchPredictions]);

  function choose(hit: NominatimHit) {
    setOpen(false);
    setOptions([]);
    setFetching(true);
    try {
      const place = toPlaceResult(hit);
      const parsed = parsePlace(place);
      // Fall back on the first `display_name` segment when the parser
      // couldn't compose a location (rare: hit had no address_components
      // beyond a country / no country at all).
      const label = parsed.location || cleanDisplayName(hit.display_name, 3);
      setInput(label);
      onChange?.(label);
      onSelect?.(parsed);
    } finally {
      setFetching(false);
    }
  }

  return (
    <div className={cn('relative w-full', className)}>
      <Input
        {...rest}
        ref={inputRef}
        value={input}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          const next = e.target.value;
          setInput(next);
          setOpen(true);
          onChange?.(next);
        }}
        onFocus={() => setOpen(true)}
        // Defer the close so a click on a prediction (onMouseDown) lands first.
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={cn(inputClassName)}
      />
      {fetching ? (
        <Loader2
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
          aria-hidden="true"
        />
      ) : null}
      {open && ready && options.length > 0 ? (
        <ul
          role="listbox"
          className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-md border border-border bg-surface py-1 shadow-lg"
        >
          {options.map((opt) => {
            const { mainText, secondaryText } = splitDisplayName(opt.display_name);
            return (
              <li key={opt.place_id} role="option" aria-selected="false">
                <button
                  type="button"
                  // onMouseDown fires before the input's onBlur closes the list.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(opt);
                  }}
                  className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-surface-2"
                >
                  <MapPin
                    className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-ink">
                      {mainText}
                    </span>
                    {secondaryText ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {secondaryText}
                      </span>
                    ) : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
});
